import { longestCommonSubsequence } from './longestCommonSubsequence';
import type { DiffableArray } from './types';

export interface ListDiffEqual<BeforeValue> {
    op: '=';
    values: Array<BeforeValue>;
}

export interface ListDiffInsert<AfterValue> {
    op: '+';
    values: Array<AfterValue>;
}

export interface ListDiffDelete<BeforeValue> {
    op: '-';
    values: Array<BeforeValue>;
}

export type ListDiff<BeforeValue, AfterValue = BeforeValue> =
    | ListDiffEqual<BeforeValue>
    | ListDiffInsert<AfterValue>
    | ListDiffDelete<BeforeValue>;

/**
 * Compute the diff between 2 ordered lists/arrays.
 */
export function diffOrderedLists<BeforeValue, AfterValue>(
    before: DiffableArray<BeforeValue>,
    after: DiffableArray<AfterValue>,
    // @ts-expect-error
    areEquals: (a: BeforeValue, b: AfterValue) => boolean = (a, b) => a === b
): Array<ListDiff<BeforeValue, AfterValue>> {
    const commonSeq = longestCommonSubsequence(before, after, areEquals);

    const startBefore = commonSeq.start1;
    const startAfter = commonSeq.start2;

    if (commonSeq.length === 0) {
        const result: Array<ListDiff<BeforeValue, AfterValue>> = [];
        if (before.length) {
            result.push({ op: '-', values: before.slice() });
        }
        if (after.length) {
            result.push({ op: '+', values: after.slice() });
        }
        return result;
    }

    const beforeLeft = before.slice(0, startBefore);
    const afterLeft = after.slice(0, startAfter);
    const beforeRight = before.slice(startBefore + commonSeq.length);
    const afterRight = after.slice(startAfter + commonSeq.length);
    return [
        ...diffOrderedLists(beforeLeft, afterLeft, areEquals),
        {
            op: '=',
            values: before.slice(startBefore, startBefore + commonSeq.length),
        },
        ...diffOrderedLists(beforeRight, afterRight, areEquals),
    ];
}
